import { useState, useEffect } from "react";
import { useSearch } from "wouter";
import {
  cancelBooking,
  rescheduleBooking,
  getAvailableSlots,
  getBookingForManage,
  type TimeSlot,
} from "@/lib/api";
import { localDateWindow } from "@/lib/dates";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Snackbar,
  Alert,
  Typography,
  Grid,
} from "@mui/material";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

function formatDate(dateStr: string): string {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatTime(t: string): string {
  const [h, m] = t.split(":").map(Number);
  const isPM = h! >= 12;
  const hour12 = h! % 12 || 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${isPM ? "PM" : "AM"}`;
}

function getNext14Days(): { start: string; end: string } {
  const days = localDateWindow(14);
  return { start: days[0]!, end: days[days.length - 1]! };
}

type ManageAction = "cancel" | "reschedule";
type PageState = "idle" | "cancelling" | "rescheduling" | "done";

const MANAGE_ACTIONS: readonly ManageAction[] = ["cancel", "reschedule"];

/**
 * Only the two actions the page can actually carry out are accepted. A link
 * carrying anything else is a broken or tampered-with link, and must reach the
 * "Invalid link" screen rather than falling through to a blank page.
 */
function parseAction(value: string | null): ManageAction | null {
  return MANAGE_ACTIONS.includes(value as ManageAction) ? (value as ManageAction) : null;
}

interface SnackState {
  message: string;
  severity: "error" | "success" | "info";
}

export default function ManagePage() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const action = parseAction(params.get("action"));
  const bookingId = params.get("id") ?? "";
  const token = params.get("token") ?? "";
  const pharmacyId = params.get("pharmacyId") ?? "";

  const [pageState, setPageState] = useState<PageState>("idle");
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [snack, setSnack] = useState<SnackState | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  // A technical failure must never render as "No availability" — that reads
  // to the patient (and to whoever investigates the report) as a genuinely
  // empty schedule. Held separately from the snackbar so the message stays
  // on screen after the toast auto-hides.
  const [slotsError, setSlotsError] = useState<string | null>(null);

  const notify = (msg: string, severity: SnackState["severity"] = "error") =>
    setSnack({ message: msg, severity });

  useEffect(() => {
    if (action !== "reschedule" || !pharmacyId || !bookingId || !token) return;

    let cancelled = false;
    setSlotsLoading(true);
    setSlotsError(null);

    (async () => {
      // Scope the picker to THIS booking: its own assigned pharmacist and
      // its own original appointment length. Asking for the union of every
      // pharmacist's slots at a fixed 30 minutes (the previous behaviour)
      // showed times the reschedule submission would then reject, and hid
      // times the assigned pharmacist actually had free.
      const booking = await getBookingForManage({ pharmacyId, bookingId, token });
      const { start, end } = getNext14Days();
      const slots = await getAvailableSlots(
        pharmacyId,
        start,
        end,
        booking.durationMinutes,
        booking.pharmacistId ?? undefined,
      );
      if (!cancelled) setAvailableSlots(slots.filter((s) => s.available));
    })()
      .catch((err: unknown) => {
        // getBookingForManage / getAvailableSlots (lib/api.ts) already log
        // the request/error detail (component: "booking-widget"); surface it
        // to the patient too so a real failure is visibly distinct from an
        // empty schedule.
        const msg =
          err instanceof Error && err.message
            ? err.message
            : "Could not load available times.";
        console.error("[booking-widget] reschedule loadSlots failed", { pharmacyId, message: msg });
        if (cancelled) return;
        setSlotsError(msg);
        notify(msg);
      })
      .finally(() => {
        if (!cancelled) setSlotsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [action, pharmacyId, bookingId, token]);

  // Every backend call this page makes is scoped by pharmacyId, so a link
  // missing it would otherwise reach the functions with an empty tenant.
  if (!action || !pharmacyId || !bookingId || !token) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.50",
        }}
      >
        <Box sx={{ textAlign: "center", p: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Invalid link
          </Typography>
          <Typography color="text.secondary">
            This booking management link is invalid or has expired.
          </Typography>
        </Box>
      </Box>
    );
  }

  const handleCancel = async () => {
    setLoading(true);
    try {
      await cancelBooking({ pharmacyId, bookingId, token });
      setMessage("Your booking has been cancelled successfully.");
      setPageState("done");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Cancellation failed";
      notify(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedSlot) return;
    setLoading(true);
    try {
      await rescheduleBooking({
        pharmacyId,
        bookingId,
        token,
        newDate: selectedSlot.date,
        newStartTime: selectedSlot.startTime,
        newEndTime: selectedSlot.endTime,
      });
      setMessage(
        `Your booking has been rescheduled to ${formatDate(selectedSlot.date)} at ${formatTime(selectedSlot.startTime)}.`,
      );
      setPageState("done");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Reschedule failed";
      notify(msg);
    } finally {
      setLoading(false);
    }
  };

  if (pageState === "done") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.50",
        }}
      >
        <Box sx={{ textAlign: "center", p: 4, maxWidth: 448 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "success.main", mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
            Done
          </Typography>
          <Typography color="text.secondary">{message}</Typography>
        </Box>
      </Box>
    );
  }

  if (action === "cancel") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.50",
        }}
      >
        <Box sx={{ textAlign: "center", p: 4, maxWidth: 448 }}>
          <EventBusyIcon sx={{ fontSize: 56, color: "text.secondary", mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
            Cancel booking
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Are you sure you want to cancel your consultation? This action cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
            <Button variant="outlined" onClick={() => window.history.back()}>
              Go back
            </Button>
            <Button variant="contained" color="error" onClick={handleCancel} disabled={loading}>
              {loading ? "Cancelling…" : "Yes, cancel booking"}
            </Button>
          </Box>
        </Box>
        <Snackbar
          open={!!snack}
          onClose={() => setSnack(null)}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snack?.severity} onClose={() => setSnack(null)}>
            {snack?.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  if (action === "reschedule") {
    const slotsByDate: Record<string, TimeSlot[]> = {};
    for (const slot of availableSlots) {
      if (!slotsByDate[slot.date]) slotsByDate[slot.date] = [];
      slotsByDate[slot.date]!.push(slot);
    }
    const dates = Object.keys(slotsByDate).sort();

    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
        <Box sx={{ bgcolor: "primary.main", color: "white", px: 3, py: 4 }}>
          <Container maxWidth="md">
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Reschedule booking
            </Typography>
            <Typography sx={{ opacity: 0.8, mt: 0.5 }}>
              Choose a new date and time
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="md" sx={{ py: 4 }}>
          {selectedSlot ? (
            <Box>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                  New time selected:
                </Typography>
                <Typography color="text.secondary">
                  {formatDate(selectedSlot.date)} at {formatTime(selectedSlot.startTime)}
                </Typography>
              </Paper>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button
                  variant="outlined"
                  onClick={() => setSelectedSlot(null)}
                  sx={{ flex: 1 }}
                >
                  Choose different time
                </Button>
                <Button
                  variant="contained"
                  onClick={handleReschedule}
                  disabled={loading}
                  sx={{ flex: 1 }}
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  {loading ? "Rescheduling…" : "Confirm reschedule"}
                </Button>
              </Box>
            </Box>
          ) : slotsLoading ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading available times…
              </Typography>
            </Box>
          ) : slotsError ? (
            // A technical failure — deliberately NOT the "No availability"
            // empty state, which would misreport a broken request as a full
            // diary and leave the patient with no actionable next step.
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                {slotsError}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                We couldn&apos;t load available times. Please refresh to try again, or contact the
                pharmacy directly.
              </Typography>
            </Box>
          ) : dates.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CalendarMonthIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
              <Typography sx={{ fontWeight: 500, mb: 0.5 }}>
                No availability
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No open slots in the next 14 days. Please contact the pharmacy directly.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {dates.map((date) => (
                <Box key={date}>
                  <Typography sx={{ fontWeight: 700, mb: 1.5 }}>
                    {formatDate(date)}
                  </Typography>
                  <Grid container spacing={1}>
                    {slotsByDate[date]!.map((slot) => (
                      <Grid
                        size={{ xs: 4, sm: 3 }}
                        key={`${slot.startTime}-${slot.endTime}`}
                      >
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => setSelectedSlot(slot)}
                          sx={{
                            borderRadius: 2,
                            fontWeight: "medium",
                            fontSize: "0.875rem",
                          }}
                        >
                          {formatTime(slot.startTime)}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </Box>
          )}
        </Container>

        <Snackbar
          open={!!snack}
          onClose={() => setSnack(null)}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snack?.severity} onClose={() => setSnack(null)}>
            {snack?.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return null;
}
